/**
 * ============================================================================
 * TaskFlow ERP
 * Module : Campaign Management
 * Feature: Prospect Import
 *
 * File:
 * src/modules/campaign/prospectImport/components/importWorkspace/drawers/EditDrawer.jsx
 *
 * Description:
 * Edit Drawer for Imported Prospect.
 *
 * Responsibilities
 * ----------------
 * • Edit imported prospect information
 * • Validate mandatory fields
 * • Save changes
 * • Close drawer
 *
 * No API calls.
 * Business logic remains in Service layer.
 * ============================================================================
 */

import React, {
    useEffect,
    useState
} from "react";

import {
    Offcanvas,
    Card,
    Row,
    Col,
    Form,
    Button
} from "react-bootstrap";

export default function EditDrawer({

    show,

    config,

    state,

    actions

}) {

    // ==========================================================
    // STATE
    // ==========================================================

    const {

        selectedRecord

    } = state;

    // ==========================================================
    // ACTIONS
    // ==========================================================

    const {

        closeEditDrawer,

        updateImportedProspect

    } = actions;

    // ==========================================================
    // LOCAL FORM STATE
    // ==========================================================

    const [form, setForm] = useState({

        name: "",

        external_id: "",

        contacts: "",

        industry: "",

        lead_source: "",

        status: "",

        remarks: ""

    });

    // ==========================================================
    // LOAD RECORD
    // ==========================================================

    useEffect(() => {

        if (!selectedRecord) {

            return;

        }

        setForm({

            name: selectedRecord.name || "",

            external_id:
                selectedRecord.external_id || "",

            contacts:
                selectedRecord.contacts || "",

            industry:
                selectedRecord.industry || "",

            lead_source:
                selectedRecord.lead_source || "",

            status:
                selectedRecord.status || "",

            remarks:
                selectedRecord.remarks || ""

        });

    }, [

        selectedRecord

    ]);

    // ==========================================================
    // CHANGE HANDLER
    // ==========================================================

    const handleChange = (event) => {

        const {

            name,

            value

        } = event.target;

        setForm(previous => ({

            ...previous,

            [name]: value

        }));

    };

    if (!selectedRecord) {

        return null;

    }

    return (

        <Offcanvas

            show={show}

            onHide={closeEditDrawer}

            placement="end"

            backdrop={true}

            scroll={false}

            style={{

                width: "800px"

            }}

        >

            {/* ====================================================== */}
            {/* HEADER */}
            {/* ====================================================== */}

            <Offcanvas.Header closeButton>

                <Offcanvas.Title>

                    <i className="fas fa-edit me-2 text-warning"></i>

                    Edit Imported Prospect

                </Offcanvas.Title>

            </Offcanvas.Header>

            <Offcanvas.Body>

                {/* ================================================== */}
                {/* GENERAL INFORMATION */}
                {/* ================================================== */}

                <Card className="mb-4">

                    <Card.Header>

                        <strong>

                            General Information

                        </strong>

                    </Card.Header>

                    <Card.Body>

                        <Row className="mb-3">

                            <Col md={4}>

                                <Form.Label>

                                    Prospect Name
                                    <span className="text-danger">

                                        *

                                    </span>

                                </Form.Label>

                            </Col>

                            <Col md={8}>

                                <Form.Control

                                    name="name"

                                    value={form.name}

                                    onChange={handleChange}

                                />

                            </Col>

                        </Row>

                        <Row className="mb-3">

                            <Col md={4}>

                                <Form.Label>

                                    External ID

                                </Form.Label>

                            </Col>

                            <Col md={8}>

                                <Form.Control

                                    name="external_id"

                                    value={form.external_id}

                                    onChange={handleChange}

                                />

                            </Col>

                        </Row>

                        <Row className="mb-3">

                            <Col md={4}>

                                <Form.Label>

                                    Contacts

                                </Form.Label>

                            </Col>

                            <Col md={8}>

                                <Form.Control

                                    type="number"

                                    name="contacts"

                                    value={form.contacts}

                                    onChange={handleChange}

                                />

                            </Col>

                        </Row>
                                                <Row className="mb-3">

                            <Col md={4}>

                                <Form.Label>

                                    Industry

                                </Form.Label>

                            </Col>

                            <Col md={8}>

                                <Form.Select

                                    name="industry"

                                    value={form.industry}

                                    onChange={handleChange}

                                >

                                    <option value="">

                                        Select Industry

                                    </option>

                                    <option>

                                        Manufacturing

                                    </option>

                                    <option>

                                        Information Technology

                                    </option>

                                    <option>

                                        Healthcare

                                    </option>

                                    <option>

                                        Banking

                                    </option>

                                    <option>

                                        Retail

                                    </option>

                                    <option>

                                        Construction

                                    </option>

                                </Form.Select>

                            </Col>

                        </Row>

                        <Row className="mb-3">

                            <Col md={4}>

                                <Form.Label>

                                    Lead Source

                                </Form.Label>

                            </Col>

                            <Col md={8}>

                                <Form.Select

                                    name="lead_source"

                                    value={form.lead_source}

                                    onChange={handleChange}

                                >

                                    <option value="">

                                        Select Lead Source

                                    </option>

                                    <option>

                                        Website

                                    </option>

                                    <option>

                                        Referral

                                    </option>

                                    <option>

                                        Email Campaign

                                    </option>

                                    <option>

                                        Trade Show

                                    </option>

                                    <option>

                                        Cold Call

                                    </option>

                                    <option>

                                        Social Media

                                    </option>

                                </Form.Select>

                            </Col>

                        </Row>

                        <Row>

                            <Col md={4}>

                                <Form.Label>

                                    Status

                                </Form.Label>

                            </Col>

                            <Col md={8}>

                                <Form.Select

                                    name="status"

                                    value={form.status}

                                    onChange={handleChange}

                                >

                                    <option>

                                        Verified

                                    </option>

                                    <option>

                                        Warning

                                    </option>

                                    <option>

                                        Duplicate

                                    </option>

                                    <option>

                                        Invalid

                                    </option>

                                </Form.Select>

                            </Col>

                        </Row>

                    </Card.Body>

                </Card>

                {/* ====================================================== */}
                {/* VALIDATION INFORMATION */}
                {/* ====================================================== */}

                <Card className="mb-4">

                    <Card.Header>

                        <strong>

                            Validation Information

                        </strong>

                    </Card.Header>

                    <Card.Body>

                        <Row className="mb-3">

                            <Col md={5}>

                                Validation Status

                            </Col>

                            <Col md={7}>

                                <span className="text-success">

                                    <i className="fas fa-check-circle me-2"></i>

                                    Verified

                                </span>

                            </Col>

                        </Row>

                        <Row className="mb-3">

                            <Col md={5}>

                                Duplicate Check

                            </Col>

                            <Col md={7}>

                                {

                                    form.status === "Duplicate"

                                        ? (

                                            <span className="text-warning">

                                                <i className="fas fa-exclamation-circle me-2"></i>

                                                Duplicate Found

                                            </span>

                                        )

                                        : (

                                            <span className="text-success">

                                                <i className="fas fa-check-circle me-2"></i>

                                                Passed

                                            </span>

                                        )

                                }

                            </Col>

                        </Row>

                        <Row>

                            <Col md={5}>

                                Business Validation

                            </Col>

                            <Col md={7}>

                                {

                                    form.status === "Invalid"

                                        ? (

                                            <span className="text-danger">

                                                <i className="fas fa-times-circle me-2"></i>

                                                Failed

                                            </span>

                                        )

                                        : (

                                            <span className="text-success">

                                                <i className="fas fa-check-circle me-2"></i>

                                                Passed

                                            </span>

                                        )

                                }

                            </Col>

                        </Row>

                    </Card.Body>

                </Card>

                {/* ====================================================== */}
                {/* REMARKS */}
                {/* ====================================================== */}

                <Card>

                    <Card.Header>

                        <strong>

                            Remarks

                        </strong>

                    </Card.Header>

                    <Card.Body>

                        <Form.Control

                            as="textarea"

                            rows={4}

                            name="remarks"

                            value={form.remarks}

                            onChange={handleChange}

                            placeholder="Enter remarks..."

                        />

                    </Card.Body>

                </Card>
                                {/* ====================================================== */}
                {/* FOOTER */}
                {/* ====================================================== */}

                <div className="d-flex justify-content-between mt-4">

                    <Button

                        variant="secondary"

                        onClick={closeEditDrawer}

                    >

                        <i className="fas fa-times me-2"></i>

                        Cancel

                    </Button>

                    <Button

                        variant="success"

                        onClick={() =>

                            updateImportedProspect(form)

                        }

                    >

                        <i className="fas fa-save me-2"></i>

                        Save Changes

                    </Button>

                </div>

            </Offcanvas.Body>

        </Offcanvas>

    );

}